import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/store";

const CATS = ["electronics", "outdoors", "music", "tools", "vehicles", "fashion"];
const IMG_SUGGEST = "https://images.unsplash.com/photo-1504148455328-c376907d081c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function CreateListing() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", category: "electronics", price_per_day: "", price_per_week: "",
    currency: "AUD", country: "AU", city: "", postal_code: "", delivery_terms: "", payment_terms: "",
    contact_links: { website: "", whatsapp: "", instagram: "", facebook: "", tiktok: "" },
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLink = (k, v) => setForm((f) => ({ ...f, contact_links: { ...f.contact_links, [k]: v } }));
  const inputCls = "w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-colors focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20";

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(`${API}/files/${data.path}`);
      }
      setPhotos((p) => [...p, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price_per_day: parseFloat(form.price_per_day),
        price_per_week: form.price_per_week ? parseFloat(form.price_per_week) : null,
        photos: photos.length ? photos : [IMG_SUGGEST],
      };
      const { data } = await api.post("/listings", payload);
      toast.success("Listing published!");
      navigate(`/listing/${data.listing_id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to publish"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("create_title")}</h1>
      <p className="mt-2 text-gray-500">Set your price in your local currency. Renters worldwide will see it converted automatically.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input data-testid="cl-title" required placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
        <textarea data-testid="cl-description" required placeholder="Description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="cl-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
            {CATS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
          <select data-testid="cl-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls}>
            <option value="BR">🇧🇷 Brazil</option>
            <option value="AU">🇦🇺 Australia</option>
            <option value="US">🇺🇸 United States</option>
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <input data-testid="cl-price" required type="number" min="0" step="0.01" placeholder="Price / day" value={form.price_per_day} onChange={(e) => set("price_per_day", e.target.value)} className={`col-span-2 ${inputCls}`} />
          <select data-testid="cl-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls}>
            <option value="BRL">BRL</option>
            <option value="AUD">AUD</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <input data-testid="cl-price-week" type="number" min="0" step="0.01" placeholder={`Price / week (optional) — ${form.currency}`} value={form.price_per_week} onChange={(e) => set("price_per_week", e.target.value)} className={inputCls} />
        <textarea data-testid="cl-payment-terms" placeholder="Payment, deposit & bond terms — you arrange payment directly with renters, so describe your own rules here" rows={3} value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className={inputCls} />
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">Your links (optional) — website & social media</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <input data-testid="cl-link-website" placeholder="Website URL" value={form.contact_links.website} onChange={(e) => setLink("website", e.target.value)} className={inputCls} />
            <input data-testid="cl-link-whatsapp" placeholder="WhatsApp (number or link)" value={form.contact_links.whatsapp} onChange={(e) => setLink("whatsapp", e.target.value)} className={inputCls} />
            <input data-testid="cl-link-instagram" placeholder="Instagram (@ or link)" value={form.contact_links.instagram} onChange={(e) => setLink("instagram", e.target.value)} className={inputCls} />
            <input data-testid="cl-link-facebook" placeholder="Facebook" value={form.contact_links.facebook} onChange={(e) => setLink("facebook", e.target.value)} className={inputCls} />
            <input data-testid="cl-link-tiktok" placeholder="TikTok" value={form.contact_links.tiktok} onChange={(e) => setLink("tiktok", e.target.value)} className={inputCls} />
          </div>
          <p className="mt-2 text-xs text-gray-400">These let renters reach you directly on your own website or social media.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="cl-city" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
          <input data-testid="cl-postal" placeholder="Postal code (CEP / Postcode)" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className={inputCls} />
        </div>
        <textarea data-testid="cl-delivery" placeholder="Delivery & pickup terms (e.g. pickup location, delivery radius, deposit)" rows={3} value={form.delivery_terms} onChange={(e) => set("delivery_terms", e.target.value)} className={inputCls} />

        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">{t("photos")}</div>
          <div className="flex flex-wrap gap-3">
            {photos.map((p, i) => (
              <div key={i} className="relative h-24 w-24 overflow-hidden rounded-xl border border-gray-200">
                <img src={p} alt="" className="h-full w-full object-cover" />
                <button type="button" data-testid={`remove-photo-${i}`} onClick={() => removePhoto(i)} className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white"><X size={12} /></button>
              </div>
            ))}
            <label data-testid="cl-photo-upload" className="grid h-24 w-24 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">
              <input type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" />
              <Upload size={20} />
            </label>
          </div>
          {uploading && <div className="mt-2 text-xs text-gray-400">Uploading…</div>}
        </div>
        <button data-testid="cl-submit" disabled={loading || uploading} className="w-full rounded-full bg-[#FF3B30] py-3.5 font-bold text-white transition-colors hover:bg-[#E6352B] disabled:opacity-60">{loading ? "Publishing…" : "Publish listing"}</button>
      </form>
    </div>
  );
}
